// Profile/role cache keyed by MSAL upn; lets Redux hydrate before meta resolves so axios headers are correct and fetches can race with meta.
import LZString from "lz-string";

const CACHE_KEY_PREFIX = "kural-profile-cache-v1:";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export interface ProfileCache {
	role: string;
	userProfile: string;
	userProfileId: string;
	supportedLanguages?: string[];
	timestamp: number;
}

const getKey = (upn: string) => `${CACHE_KEY_PREFIX}${upn.toLowerCase()}`;

// Mirrors the LZString.compress/decompress pipeline used by the redux-persist transform in src/store
// so this cache isn't sitting in localStorage as plain readable JSON. Falls back to raw JSON.parse
// so entries written before compression was introduced still hydrate on the next read.
const decode = (raw: string): ProfileCache | null => {
	const decompressed = LZString.decompress(raw);
	if (decompressed) {
		try {
			return JSON.parse(decompressed) as ProfileCache;
		} catch {
			// not a compressed JSON payload — fall through to plain-JSON fallback
		}
	}
	try {
		return JSON.parse(raw) as ProfileCache;
	} catch {
		return null;
	}
};

export const readProfileCache = (upn: string | undefined | null): ProfileCache | null => {
	if (!upn) return null;
	try {
		const raw = localStorage.getItem(getKey(upn));
		if (!raw) return null;
		const parsed = decode(raw);
		if (!parsed) {
			localStorage.removeItem(getKey(upn));
			return null;
		}
		if (!parsed.timestamp || Date.now() - parsed.timestamp > CACHE_TTL_MS) {
			localStorage.removeItem(getKey(upn));
			return null;
		}
		if (!parsed.role || !parsed.userProfileId) return null;
		return parsed;
	} catch {
		return null;
	}
};

export const writeProfileCache = (
	upn: string | undefined | null,
	data: Omit<ProfileCache, "timestamp">,
) => {
	if (!upn) return;
	try {
		const payload = JSON.stringify({ ...data, timestamp: Date.now() });
		const compressed = LZString.compress(payload);
		// Compress should always yield a non-empty string for non-empty input; fall back to the raw
		// payload defensively so a write failure can't leave the cache permanently empty.
		localStorage.setItem(getKey(upn), compressed || payload);
	} catch {
		// quota/serialization errors — cache is best-effort
	}
};

export const clearProfileCache = (upn?: string | null) => {
	try {
		if (upn) {
			localStorage.removeItem(getKey(upn));
			return;
		}
		for (let i = localStorage.length - 1; i >= 0; i--) {
			const k = localStorage.key(i);
			if (k?.startsWith(CACHE_KEY_PREFIX)) localStorage.removeItem(k);
		}
	} catch {
		// ignore
	}
};
